import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { ChevronLeft, Lock, Truck, Loader2, Tag, X, Check, AlertTriangle } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { STORE_CONFIG } from '../config/storeConfig';

// Sales tax rate
const TAX_RATE = 0.0875; // 8.75%

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, getTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
  });

  // Shipping state
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [ratesError, setRatesError] = useState(null);

  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState(null);

  // Calculate totals including tax
  const subtotal = getTotal();
  const discountAmount = appliedDiscount?.discount_amount || 0;
  const shippingCost = selectedShipping?.rate || 0;
  
  // Check if order is international (non-US)
  const isInternational = formData.country && formData.country.toLowerCase() !== 'united states' && formData.country.toLowerCase() !== 'usa' && formData.country.toLowerCase() !== 'us';
  
  // Calculate tax - applies only to US orders
  const taxInfo = useMemo(() => {
    if (isInternational) {
      return { rate: 0, amount: 0, applicable: false };
    }
    const taxableAmount = subtotal - discountAmount; // Tax on products after discount
    const taxAmount = Math.round(taxableAmount * TAX_RATE * 100) / 100; // Round to cents
    return { rate: TAX_RATE, amount: taxAmount, applicable: true };
  }, [subtotal, discountAmount, isInternational]);
  
  const grandTotal = subtotal - discountAmount + taxInfo.amount + shippingCost;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear shipping rates when address changes
    if (['address', 'city', 'state', 'zip_code'].includes(name)) {
      setShippingRates([]);
      setSelectedShipping(null);
    }
    // Clear discount error when email changes (for re-validation)
    if (name === 'email') {
      setDiscountError(null);
    }
  };

  // Apply discount code
  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setDiscountError('Please enter your email address first');
      return;
    }

    setDiscountLoading(true);
    setDiscountError(null);

    try {
      const result = await api.validateDiscountCode(discountCode, formData.email, subtotal);
      setAppliedDiscount(result);
      toast.success(result.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid discount code';
      setDiscountError(errorMsg);
      setAppliedDiscount(null);
    } finally {
      setDiscountLoading(false);
    }
  };

  // Remove applied discount
  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError(null);
  };

  // Fetch shipping rates when address is complete
  const fetchShippingRates = async () => {
    if (!formData.address || !formData.city || !formData.state || !formData.zip_code) {
      toast.error('Please fill in your complete shipping address first');
      return;
    }

    setLoadingRates(true);
    setRatesError(null);

    try {
      // Pass cart items to backend for accurate dimension calculation
      const rates = await api.getShippingRates(formData, items);
      setShippingRates(rates);
      
      // Auto-select cheapest option
      if (rates.length > 0) {
        setSelectedShipping(rates[0]);
      }
    } catch (error) {
      console.error('Failed to get shipping rates:', error);
      setRatesError('Unable to calculate shipping. Please check your address.');
    } finally {
      setLoadingRates(false);
    }
  };

  const validateForm = () => {
    const required = ['email', 'first_name', 'last_name', 'address', 'city', 'state', 'zip_code'];
    for (const field of required) {
      if (!formData[field].trim()) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }
    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!selectedShipping) {
      toast.error('Please select a shipping method');
      return false;
    }
    return true;
  };

  const createOrder = async (data, actions) => {
    if (!validateForm()) {
      throw new Error('Please fill in all required fields');
    }
    return actions.order.create({
      purchase_units: [
        {
          amount: {
            value: grandTotal.toFixed(2),
          },
          description: `Only Skullz Order - ${items.length} item(s)`,
        },
      ],
    });
  };

  const onApprove = async (data, actions) => {
    setLoading(true);
    try {
      const details = await actions.order.capture();
      
      // Apply discount code usage if one was used
      if (appliedDiscount) {
        try {
          await api.applyDiscountCode(appliedDiscount.code, formData.email, subtotal);
        } catch (discountError) {
          console.warn('Failed to track discount usage:', discountError);
        }
      }
      
      // Create order in our database
      if (process.env.NODE_ENV === 'development') {
        console.log('Creating order with shipping_rate_id:', selectedShipping?.object_id);
      }
      await api.createOrder({
        items: items.map((item) => ({
          product_id: item.product.originalProductId || item.product.id,
          quantity: item.quantity,
          is_marketplace: item.product.is_marketplace || false,
          product_name: item.product.title,
          unit_price: item.product.price,
          is_bto: item.product.btoOrder || false,
          bto_notes: item.product.specialNotes || null,
          color_preference: item.product.colorPreference || null,
        })),
        customer_info: formData,
        total: grandTotal,
        subtotal: subtotal,
        discount_code: appliedDiscount?.code || null,
        discount_amount: discountAmount,
        tax_amount: taxInfo.amount,
        tax_rate: taxInfo.rate,
        shipping_cost: selectedShipping?.rate || 0,
        shipping_method: selectedShipping ? `${selectedShipping.carrier} ${selectedShipping.service}` : null,
        shipping_rate_id: selectedShipping?.object_id || null,  // For automatic label purchase
        paypal_order_id: details.id,
      });

      clearCart();
      navigate('/order-confirmation', {
        state: {
          orderDetails: details,
          items: items,
          total: grandTotal,
          subtotal: subtotal,
          discount: appliedDiscount,
          shipping: selectedShipping,
        },
      });
      toast.success('Order placed successfully!');
    } catch (error) {
      console.error('Order creation failed:', error);
      toast.error('Failed to process order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="empty-checkout" className="pt-20 min-h-screen bg-void">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <h1 className="font-heading text-3xl text-white mb-4">Your cart is empty</h1>
          <p className="text-neutral-400 mb-8">Add some items to your cart before checking out.</p>
          <Link
            to="/shop"
            className="inline-block bg-cyan-400 text-black px-8 py-3 font-mono uppercase tracking-widest text-sm hover:bg-cyan-300 transition-all"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  // Store is paused - show message
  if (!STORE_CONFIG.STORE_OPEN) {
    return (
      <div data-testid="store-paused-checkout" className="pt-20 min-h-screen bg-void">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h1 className="font-heading text-3xl text-white mb-4">{STORE_CONFIG.PAUSED_BUTTON_TEXT}</h1>
          <p className="text-neutral-400 mb-8">{STORE_CONFIG.PAUSED_MESSAGE}</p>
          <Link
            to="/shop"
            className="inline-block bg-cyan-400 text-black px-8 py-3 font-mono uppercase tracking-widest text-sm hover:bg-cyan-300 transition-all"
          >
            Continue Browsing
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-20 min-h-screen bg-void">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link
          to="/shop"
          className="inline-flex items-center text-neutral-400 hover:text-cyan-400 transition-colors mb-8"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Continue Shopping
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Form */}
          <div>
            <h1 className="font-heading text-3xl text-white mb-8">Checkout</h1>

            {/* Contact Information */}
            <div className="mb-8">
              <h2 className="font-mono text-sm uppercase tracking-widest text-cyan-400 mb-4">
                Contact Information
              </h2>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email address"
                data-testid="checkout-email"
                className="w-full bg-void-paper border border-white/20 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-cyan-400 focus:outline-none mb-4"
              />
            </div>

            {/* Shipping Address */}
            <div className="mb-8">
              <h2 className="font-mono text-sm uppercase tracking-widest text-cyan-400 mb-4">
                Shipping Address
              </h2>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  placeholder="First name"
                  data-testid="checkout-first-name"
                  className="w-full bg-void-paper border border-white/20 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-cyan-400 focus:outline-none"
                />
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  placeholder="Last name"
                  data-testid="checkout-last-name"
                  className="w-full bg-void-paper border border-white/20 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-cyan-400 focus:outline-none"
                />
              </div>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Street address"
                data-testid="checkout-address"
                className="w-full bg-void-paper border border-white/20 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-cyan-400 focus:outline-none mb-4"
              />
              <div className="grid grid-cols-3 gap-4 mb-4">
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City"
                  data-testid="checkout-city"
                  className="w-full bg-void-paper border border-white/20 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-cyan-400 focus:outline-none"
                />
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="State"
                  data-testid="checkout-state"
                  className="w-full bg-void-paper border border-white/20 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-cyan-400 focus:outline-none"
                />
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  placeholder="ZIP Code"
                  data-testid="checkout-zip"
                  className="w-full bg-void-paper border border-white/20 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-cyan-400 focus:outline-none"
                />
              </div>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Country"
                data-testid="checkout-country"
                className="w-full bg-void-paper border border-white/20 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-cyan-400 focus:outline-none"
              />
              
              {/* International Order Warning */}
              {isInternational && (
                <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded">
                  <div className="flex items-start gap-3">
                    <span className="text-amber-500 text-xl">⚠️</span>
                    <div>
                      <h4 className="font-mono text-sm uppercase tracking-widest text-amber-500 font-bold mb-2">
                        International Order Notice
                      </h4>
                      <p className="text-neutral-300 text-sm leading-relaxed">
                        International orders may be subject to additional taxes, duties, and tariffs upon delivery. 
                        We recommend contacting us at <a href="mailto:info@onlyskullz.com" className="text-cyan-400 hover:underline">info@onlyskullz.com</a> before 
                        placing your order to discuss shipping costs and any applicable fees for your country.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Shipping Method */}
            <div className="mb-8">
              <h2 className="font-mono text-sm uppercase tracking-widest text-cyan-400 mb-4">
                Shipping Method
              </h2>
              
              {/* Get Rates Button */}
              {shippingRates.length === 0 && (
                <button
                  onClick={fetchShippingRates}
                  disabled={loadingRates}
                  className="w-full bg-void-paper border border-cyan-400/50 text-cyan-400 px-4 py-3 font-mono uppercase tracking-widest text-sm hover:bg-cyan-400/10 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loadingRates ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Calculating Rates...
                    </>
                  ) : (
                    <>
                      <Truck className="w-4 h-4" />
                      Calculate Shipping
                    </>
                  )}
                </button>
              )}

              {ratesError && (
                <p className="text-red-400 text-sm mt-2">{ratesError}</p>
              )}

              {/* Shipping Options */}
              {shippingRates.length > 0 && (
                <div className="space-y-2">
                  {shippingRates.slice(0, 5).map((rate) => (
                    <label
                      key={rate.object_id}
                      className={`flex items-center justify-between p-4 border cursor-pointer transition-all ${
                        selectedShipping?.object_id === rate.object_id
                          ? 'border-cyan-400 bg-cyan-400/10'
                          : 'border-white/20 bg-void-paper hover:border-white/40'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="shipping"
                          checked={selectedShipping?.object_id === rate.object_id}
                          onChange={() => setSelectedShipping(rate)}
                          className="w-4 h-4 accent-cyan-400"
                        />
                        <div>
                          <p className="text-white font-medium">
                            {rate.carrier} {rate.service}
                          </p>
                          <p className="text-neutral-400 text-sm">
                            {rate.estimated_days 
                              ? `${rate.estimated_days} business day${rate.estimated_days > 1 ? 's' : ''}`
                              : 'Estimated delivery varies'
                            }
                          </p>
                        </div>
                      </div>
                      <span className="text-cyan-400 font-mono">${rate.rate.toFixed(2)}</span>
                    </label>
                  ))}
                  
                  <button
                    onClick={fetchShippingRates}
                    className="text-cyan-400 text-sm hover:underline mt-2"
                  >
                    Recalculate shipping
                  </button>
                </div>
              )}
            </div>

            {/* Payment */}
            <div>
              <h2 className="font-mono text-sm uppercase tracking-widest text-cyan-400 mb-4">
                Payment
              </h2>
              <div className="bg-void-paper border border-white/20 p-4">
                <PayPalScriptProvider
                  options={{
                    clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
                    currency: 'USD',
                  }}
                >
                  <PayPalButtons
                    style={{ layout: 'vertical', color: 'black' }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={(err) => {
                      console.error('PayPal error:', err);
                      toast.error('Payment failed. Please try again.');
                    }}
                    disabled={loading || !selectedShipping}
                  />
                </PayPalScriptProvider>
              </div>
              <p className="flex items-center gap-2 text-neutral-500 text-xs mt-4">
                <Lock className="w-3 h-3" />
                Secure checkout powered by PayPal
              </p>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div>
            <div className="bg-void-paper border border-white/10 p-6 sticky top-24">
              <h2 className="font-heading text-xl text-white mb-6">Order Summary</h2>

              {/* Items */}
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.product.id} className="flex gap-4">
                    <div className="w-16 h-16 bg-void border border-white/10 overflow-hidden flex-shrink-0">
                      <img
                        src={item.product.images?.[0]?.url || '/placeholder.jpg'}
                        alt={item.product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{item.product.title}</p>
                      <p className="text-neutral-400 text-sm">Qty: {item.quantity}</p>
                    </div>
                    <p className="text-white text-sm">
                      ${(item.product.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Discount Code */}
              <div className="border-t border-white/10 pt-4 mb-4">
                <h3 className="font-mono text-xs uppercase tracking-widest text-neutral-400 mb-3 flex items-center gap-2">
                  <Tag className="w-3 h-3" />
                  Discount Code
                </h3>
                {appliedDiscount ? (
                  <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 font-mono text-sm">{appliedDiscount.code}</span>
                      <span className="text-green-400 text-sm">(-${discountAmount.toFixed(2)})</span>
                    </div>
                    <button
                      onClick={removeDiscount}
                      className="text-neutral-400 hover:text-red-400 transition-colors"
                      title="Remove discount"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => {
                        setDiscountCode(e.target.value.toUpperCase());
                        setDiscountError(null);
                      }}
                      placeholder="Enter code"
                      className="flex-1 bg-void border border-white/20 px-3 py-2 text-white text-sm font-mono uppercase placeholder:text-neutral-500 focus:border-cyan-400 focus:outline-none"
                      data-testid="discount-code-checkout"
                    />
                    <button
                      onClick={applyDiscountCode}
                      disabled={discountLoading}
                      className="px-4 py-2 bg-cyan-400/20 border border-cyan-400/50 text-cyan-400 text-sm font-mono uppercase hover:bg-cyan-400/30 transition-all disabled:opacity-50 flex items-center gap-1"
                      data-testid="apply-discount-btn"
                    >
                      {discountLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
                    </button>
                  </div>
                )}
                {discountError && (
                  <p className="text-red-400 text-xs mt-2">{discountError}</p>
                )}
              </div>

              {/* Totals */}
              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400">Subtotal</span>
                  <span className="text-white">${subtotal.toFixed(2)}</span>
                </div>
                {appliedDiscount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-400 flex items-center gap-1">
                      <Tag className="w-3 h-3" />
                      Discount ({appliedDiscount.code})
                    </span>
                    <span className="text-green-400">-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                {taxInfo.applicable && (
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">
                      Sales Tax ({(taxInfo.rate * 100).toFixed(2)}%)
                    </span>
                    <span className="text-white">${taxInfo.amount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-400 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Shipping
                  </span>
                  <span className="text-white">
                    {selectedShipping 
                      ? `$${selectedShipping.rate.toFixed(2)}`
                      : 'Calculate above'
                    }
                  </span>
                </div>
              </div>

              <div className="border-t border-white/10 mt-4 pt-4">
                <div className="flex justify-between">
                  <span className="font-mono text-sm uppercase tracking-widest text-white">
                    Total
                  </span>
                  <span className="font-heading text-2xl text-white">
                    ${grandTotal.toFixed(2)}
                  </span>
                </div>
                {selectedShipping && (
                  <p className="text-neutral-500 text-xs mt-2 text-right">
                    via {selectedShipping.carrier} {selectedShipping.service}
                  </p>
                )}
                {taxInfo.applicable && (
                  <p className="text-neutral-500 text-xs mt-1 text-right">
                    Includes ${taxInfo.amount.toFixed(2)} sales tax
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
